import Code from "lucide-react/icons/code";
import Building2 from "lucide-react/icons/building-2";
import Palette from "lucide-react/icons/palette";
import { ComponentType } from "react";

export interface Chapter {
  id: number;
  title: string;
  shortTitle: string;
  part: number;
  description: string;
}

export interface Part {
  id: number;
  title: string;
  shortTitle: string;
}

export interface Persona {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  tagline: string;
  description: string;
  corePath: number[];
  optionalChapters: number[];
}

export const parts: Part[] = [
  {
    id: 1,
    title: "Foundations of Multi-Agent Systems",
    shortTitle: "Foundations",
  },
  {
    id: 2,
    title: "Building Multi-Agent Systems from Scratch",
    shortTitle: "Building",
  },
  {
    id: 3,
    title: "Evaluation, Optimization, and Responsible AI",
    shortTitle: "Eval & Ethics",
  },
  {
    id: 4,
    title: "Real-World Applications",
    shortTitle: "Applications",
  },
];

// Chapters verified against actual book content in /Users/victordibia/projects/autogenbook/dibia/chapters/
export const chapters: Chapter[] = [
  {
    id: 1,
    title: "Understanding Multi-Agent Systems",
    shortTitle: "Understanding MAS",
    part: 1,
    description:
      "Defines agents as entities that reason, act, communicate, and adapt. Covers when to use agents vs simple API calls, and guides you through building your first multi-agent system: a poet-critic collaboration.",
  },
  {
    id: 2,
    title: "Multi-Agent Patterns",
    shortTitle: "Patterns",
    part: 1,
    description:
      "Taxonomy of orchestration patterns from explicit to emergent control. Covers workflow patterns (sequential, conditional, parallel, supervisor) and autonomous patterns (round-robin, AI-driven, handoff).",
  },
  {
    id: 3,
    title: "UX Principles for Multi-Agent Systems",
    shortTitle: "UX Principles",
    part: 1,
    description:
      "Examines the shift from interface design to delegation design. Four essential principles: capability discovery, cost-aware delegation, observability and provenance, and interruptibility.",
  },
  {
    id: 4,
    title: "Building Your First Agent",
    shortTitle: "First Agent",
    part: 2,
    description:
      "Implements the core agent execution loop from scratch. Covers async-first architecture, event-based streaming, component serialization, and building the picoagents library with multi-provider support.",
  },
  {
    id: 5,
    title: "Building Computer Use Agents",
    shortTitle: "Computer Use",
    part: 2,
    description:
      "Extends agents to automate interfaces when APIs aren't enough. Covers action sequence generation, interface representation (DOM parsing, screenshots), and browser automation with Playwright.",
  },
  {
    id: 6,
    title: "Building Multi-Agent Workflows",
    shortTitle: "Workflows",
    part: 2,
    description:
      "Implements workflows as deterministic computational graphs. Builds type-safe steps with Pydantic, conditional edges for branching, parallel execution, and a workflow runner with streaming observability.",
  },
  {
    id: 7,
    title: "Building Autonomous Multi-Agent Orchestration",
    shortTitle: "Autonomous",
    part: 2,
    description:
      "Implements the orchestrator loop where AI models drive coordination at runtime. Builds composable termination conditions and round-robin orchestration with streaming observability.",
  },
  {
    id: 8,
    title: "Building Modern Web Experiences for Agent Applications",
    shortTitle: "Web UI",
    part: 2,
    description:
      "Build web interfaces for agents in ~200 lines using FastAPI and vanilla JS with SSE streaming. Covers choosing SSE vs WebSockets and introduces PicoAgents WebUI as a reference implementation.",
  },
  {
    id: 9,
    title: "But What About Multi-Agent Frameworks?",
    shortTitle: "Frameworks",
    part: 2,
    description:
      "Ten core capabilities that distinguish effective frameworks. Provides systematic evaluation criteria and decision guidelines for build-vs-buy choices without comparing specific frameworks.",
  },
  {
    id: 10,
    title: "Evaluating Multi-Agent Systems",
    shortTitle: "Evaluation",
    part: 3,
    description:
      "Introduces trajectories as the fundamental unit of evaluation. Covers evaluation-driven development, a 5-step planning framework, and building metrics and LLM judges for reference-based and reference-free evaluation.",
  },
  {
    id: 11,
    title: "Optimizing Multi-Agent Systems",
    shortTitle: "Optimization",
    part: 3,
    description:
      "Ten common failure modes and concrete fixes: inadequate instructions, model capability gaps, poor tool quality, missing termination conditions, absent memory, and more. Covers the two-level optimization framework.",
  },
  {
    id: 12,
    title: "Protocols for Distributed Agents",
    shortTitle: "Distributed",
    part: 3,
    description:
      "Addresses agents running across separate execution contexts. Covers Model Context Protocol (MCP) for tool integration and Agent-to-Agent Protocol (A2A) for cross-organizational collaboration.",
  },
  {
    id: 13,
    title: "Ethics and Responsible AI for Multi-Agent Systems",
    shortTitle: "Ethics & RAI",
    part: 3,
    description:
      "How agentic ethics differs from traditional AI ethics across controllability, action capability, domain scope, and verification. Covers sycophancy, alignment faking, and security as ethics.",
  },
  {
    id: 14,
    title: "Answering Business Questions from Unstructured Data",
    shortTitle: "Business Data",
    part: 4,
    description:
      "Build a workflow to analyze thousands of YCombinator companies. Demonstrates cost-effective pre-filtering, structured outputs with Pydantic, checkpointing, and production patterns.",
  },
  {
    id: 15,
    title: "Software Engineering Agent",
    shortTitle: "Code Agent",
    part: 4,
    description:
      "Build an agent like GitHub Copilot or Claude Code. Covers five tool categories (file ops, code execution, memory, meta-cognitive, task management), detailed prompts, and autonomous code improvement.",
  },
];

export const personas: Persona[] = [
  {
    id: "engineer",
    label: "Software Engineer",
    icon: Code,
    tagline: "Build agents from scratch, ship to production",
    description:
      "Focused on implementation? This track gets you building immediately. Start with core concepts (Ch 1), then build your first agent (Ch 4), add computer use (Ch 5), and design workflows (Ch 6). Master orchestration (Ch 7) and UIs (Ch 8), then evaluate (Ch 10-11) and build a Coding Agent (Ch 15).",
    corePath: [1, 4, 5, 6, 7, 8, 10, 11, 15],
    optionalChapters: [2, 3, 9, 12, 13, 14],
  },
  {
    id: "leader",
    label: "Tech Leader",
    icon: Building2,
    tagline: "Patterns, evaluation, and strategic decisions",
    description:
      "Focused on strategy? This track prioritizes decision-making and trade-offs. Start with technology fundamentals (Ch 1), then understand orchestration patterns (Ch 2) and UX principles (Ch 3). Learn to evaluate frameworks (Ch 9) and system performance (Ch 10), before covering distributed protocols and ethics (Ch 12-13).",
    corePath: [1, 2, 3, 9, 10, 12, 13],
    optionalChapters: [4, 5, 6, 7, 8, 11, 14, 15],
  },
  {
    id: "designer",
    label: "Designer / PM",
    icon: Palette,
    tagline: "UX principles and responsible AI",
    description:
      "Focused on user experience? This track centers on design and interaction. Start with core concepts (Ch 1), then explore orchestration patterns (Ch 2) and the four pillars of agent UX (Ch 3). See these applied in web interfaces (Ch 8) and finish with Responsible AI (Ch 13).",
    corePath: [1, 2, 3, 8, 13],
    optionalChapters: [4, 5, 6, 7, 9, 10, 11, 12, 14, 15],
  },
];

export function getChaptersByPart(partId: number): Chapter[] {
  return chapters.filter((ch) => ch.part === partId);
}

export function getPersonaById(id: string): Persona | undefined {
  return personas.find((p) => p.id === id);
}
